import React, { useEffect, useState, useContext } from 'react';
import DashboardSidebar from 'shared/components/DashboardSidebar';
import styles from './FlowContainer.module.scss';
import { DashboardContainerColorProps } from 'enums/DashboardContainerEnums';
import { Row, Col } from 'antd';
import clsx from 'clsx';
import DataMapDrawer from 'shared/components/DataMapDrawer';
import { EmissionDataContext } from 'context/EmissionDataContext';
import { useParams } from 'react-router-dom';
import { DataMappingProps } from 'enums/CreateProductStageNavigationBarEnums';
import HelpDesk from 'shared/components/Helpdesk';
import { HelpDeskContext } from 'context/HelpDeskContext';
import { CaretLeftOutlined } from '@ant-design/icons';
import { FlowContainerConstants } from './FlowContainerConstants';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import CreateProductModal from 'shared/components/CreateProductModal';
import { Product } from 'models/Product/product.model';
import { SocketContext } from 'context/SocketContext';
import { ProductRawMaterial } from 'models/ProductRawMaterial/productRawMaterial.model';
import { deserialize } from 'serializr';
import { ProductRawMaterialTransportations } from 'models/ProductRawMaterialTransportations/productRawMaterialTransportations.model';
import ProductCreationService from 'services/ProductsService/productCreation.service';
import { ProcessingFacilityEmissionData } from 'models/ProcessingFacilityEmissionData/ProcessingFacilityEmissionData.model';
import { ProductDistributionEmissionData } from 'models/ProductDistributionEmissionData/ProductDistributionEmissionData.model';

interface DashboardContainerProps {
  children: (params: {
    fetchSingleProduct: (id: number) => void;
    newProduct: Product | undefined;
  }) => React.ReactNode;
  bgColor?:
    | DashboardContainerColorProps.GREY
    | DashboardContainerColorProps.WHITE;
}

const FlowContainer: React.FC<DashboardContainerProps> = ({
  children,
  bgColor,
}: DashboardContainerProps) => {
  let productId: number;
  const { id } = useParams();
  productId = Number(id);
  const {
    STAGE_1_TOTAL,
    STAGE_2_TOTAL,
    STAGE_3_TOTAL,
    STAGE_4_TOTAL,
    B2B,
    B2C,
    STAGE_1_TOTAL_EMISSION,
    STAGE_2_TOTAL_EMISSION,
    STAGE_3_TOTAL_EMISSION,
    STAGE_4_TOTAL_EMISSION,
    HELP_DESK,
  } = FlowContainerConstants;
  const { productLoading, loading, fetchSingleProduct, newProduct } =
    ProductCreationService();
  const [modalState, setModalState] = useState<boolean>(false);

  const handleToggleModal = () => {
    setModalState(prevState => !prevState);
  };

  const contentContainer = clsx({
    [styles.contentContainer]: true,
    [styles.whiteBg]: bgColor === DashboardContainerColorProps.WHITE,
    [styles.greyBg]: bgColor === DashboardContainerColorProps.GREY,
  });
  const { isHelpDeskOpen, setIsHelpDeskOpen, helpDeskData } =
    useContext(HelpDeskContext);
  const handleToggleHelpDesk = () => {
    setIsHelpDeskOpen(!isHelpDeskOpen);
  };
  const {
    setStage1Data,
    setStage2Data,
    setStage3Data,
    setStage4Data,
    setStage1Total,
    setStage2Total,
    setStage3Total,
    setStage4Total,
    setB2b,
    setB2c,
  } = React.useContext(EmissionDataContext);

  const [showProductModal, setShowProductModal] = useState<boolean>(false);

  useEffect(() => {
    fetchSingleProduct(productId);
  }, [productId]);

  const { socket } = useContext(SocketContext);

  React.useEffect(() => {
    if (socket) {
      socket.on(
        DataMappingProps.STAGE_1,
        (data: {
          rawMaterialEmissionData: ProductRawMaterial[];
          total: number;
        }) => {
          const stage1Emissions = deserialize(
            ProductRawMaterial,
            data.rawMaterialEmissionData
          );
          localStorage.setItem(
            DataMappingProps.STAGE_1,
            JSON.stringify(stage1Emissions)
          );
          localStorage.setItem(
            STAGE_1_TOTAL_EMISSION,
            JSON.stringify(data.total)
          );
          setStage1Data(stage1Emissions);
          setStage1Total(data.total);
          localStorage.setItem(DataMappingProps.STAGE_2, JSON.stringify([]));
          setStage2Data([]);
        }
      );
      socket.on(
        DataMappingProps.STAGE_2,
        (data: {
          rawMaterial: {
            rawMaterialEmissionData: ProductRawMaterial[];
            total: number;
          };
          transport: {
            transportEmissionData: ProductRawMaterialTransportations[];
            total: number;
          };
        }) => {
          localStorage.setItem(DataMappingProps.STAGE_1, JSON.stringify([]));
          setStage1Data([]);
          localStorage.setItem(DataMappingProps.STAGE_2, JSON.stringify([]));
          setStage2Data([]);
          const rawMaterialData = deserialize(
            ProductRawMaterial,
            data.rawMaterial['rawMaterialEmissionData'] as ProductRawMaterial[]
          );
          const transportationData = deserialize(
            ProductRawMaterialTransportations,
            data.transport[
              'transportEmissionData'
            ] as ProductRawMaterialTransportations[]
          );
          setStage1Data(rawMaterialData);
          setStage1Total(data.rawMaterial['total']);

          setStage2Data(transportationData);
          setStage2Total(data.transport['total']);

          localStorage.setItem(
            DataMappingProps.STAGE_1,
            JSON.stringify(rawMaterialData)
          );
          localStorage.setItem(
            STAGE_1_TOTAL_EMISSION,
            JSON.stringify(data.rawMaterial['total'])
          );

          localStorage.setItem(
            DataMappingProps.STAGE_2,
            JSON.stringify(transportationData)
          );
          localStorage.setItem(
            STAGE_2_TOTAL_EMISSION,
            JSON.stringify(data.transport['total'])
          );
          localStorage.setItem(DataMappingProps.STAGE_3, JSON.stringify([]));
          setStage3Data({});
        }
      );

      socket.on(
        DataMappingProps.STAGE_3,
        (data: ProcessingFacilityEmissionData) => {
          setStage3Data(data);
          data?.co2e && setStage3Total(data.co2e);
          localStorage.setItem(DataMappingProps.STAGE_3, JSON.stringify(data));
          localStorage.setItem(
            STAGE_3_TOTAL_EMISSION,
            JSON.stringify(data.co2e)
          );
        }
      );
      socket.on(
        DataMappingProps.STAGE_4,
        (data: {
          distributionEmissionData: ProductDistributionEmissionData[];
          total: number;
          b2b: number;
          b2c: number;
        }) => {
          setStage4Data(data.distributionEmissionData);
          setStage4Total(data.total);
          setB2b(data.b2b);
          setB2c(data.b2c);
          localStorage.setItem(
            DataMappingProps.STAGE_4,
            JSON.stringify(data.distributionEmissionData)
          );
          localStorage.setItem(
            STAGE_4_TOTAL_EMISSION,
            JSON.stringify(data.total)
          );
          localStorage.setItem(B2B, JSON.stringify(data.b2b));
          localStorage.setItem(B2C, JSON.stringify(data.b2c));
        }
      );
    }
  }, []);

  useEffect(() => {
    const stage1Stored = localStorage.getItem(DataMappingProps.STAGE_1);
    const stage2Stored = localStorage.getItem(DataMappingProps.STAGE_2);
    const stage3Stored = localStorage.getItem(DataMappingProps.STAGE_3);
    const stage4Stored = localStorage.getItem(DataMappingProps.STAGE_4);
    const stage1Total = localStorage.getItem(STAGE_1_TOTAL);
    const stage2Total = localStorage.getItem(STAGE_2_TOTAL);
    const stage3Total = localStorage.getItem(STAGE_3_TOTAL);
    const stage4Total = localStorage.getItem(STAGE_4_TOTAL);
    const b2b = localStorage.getItem(B2B);
    const b2c = localStorage.getItem(B2C);
    if (stage1Stored) {
      setStage1Data(JSON.parse(stage1Stored));
    }
    if (stage2Stored) {
      setStage2Data(JSON.parse(stage2Stored));
    }
    if (stage3Stored) {
      setStage3Data(JSON.parse(stage3Stored));
    }
    if (stage4Stored) {
      setStage4Data(JSON.parse(stage4Stored));
    }
    if (stage1Total) {
      setStage1Total(JSON.parse(stage1Total));
    }
    if (stage2Total) {
      setStage2Total(JSON.parse(stage2Total));
    }
    if (stage3Total) {
      setStage3Total(JSON.parse(stage3Total));
    }
    if (stage4Total) {
      setStage3Total(JSON.parse(stage4Total));
    }
    if (b2b) {
      setB2b(JSON.parse(b2b));
    }
    if (b2c) {
      setB2b(JSON.parse(b2c));
    }
  }, []);

  const openProductModal = () => {
    var data = {
      image: newProduct?.image,
      id: newProduct?.id,
      productName: newProduct?.name,
      description: newProduct?.description,
      brand: newProduct?.brandId,
      productNetWeight: newProduct?.netWeight,
      ml: newProduct?.netWeightUnit,
      productGrossWeight: newProduct?.grossWeight,
      temperatureControl: newProduct?.temperatureControl,
      reportingPeriodProduction: newProduct?.reportingPeriodUnits,
      percentageProcessingFacilityCostGood:
        newProduct?.processingFacilityCostPercentage,
      reportingYear: newProduct?.reportingYear,
      processingFacility: newProduct?.processingFacilityId,
      productDensity: newProduct?.density,
      status: newProduct?.status,
      uniqueId: newProduct?.uniqueId,
    };

    localStorage.setItem('create-product', JSON.stringify(data));
    setShowProductModal(true);
  };

  const user = localStorage.getItem('userDetails');
  let parsedUser: any;
  if (user !== null) {
    parsedUser = JSON.parse(user);
  }

  return (
    <Row>
      <Col span={1} className={styles.sidebarContainer}>
        <DashboardSidebar />
      </Col>

      {productLoading || loading ? (
        <div className={styles.loaderContainer}>
          <LoaderAnimation />
        </div>
      ) : (
        <Col span={23} className={contentContainer}>
          <Row>
            <div className={styles.createProductTopRow}>
              <div className={styles.topLeftContainer}>
                <h2>{newProduct?.name}</h2>
                <span
                  className={clsx(styles.productInfo, 'ml-4')}
                  onClick={openProductModal}>
                  Product info
                </span>
              </div>
            </div>

            {children({ fetchSingleProduct, newProduct })}
          </Row>
          <div
            className={clsx({
              [styles.bottomBarNav]: true,
              [styles.isOpen]: modalState,
            })}>
            <DataMapDrawer
              handleToggleModal={handleToggleModal}
              toggleStatus={modalState}
              productId={productId}
              stage={newProduct && newProduct?.stage}
              status={newProduct && newProduct?.status}
              newProduct={newProduct}
            />
          </div>
        </Col>
      )}
      <>
        <div
          className={clsx({
            [styles.helpdeskContainer]: true,
            [styles.openHelpDesk]: isHelpDeskOpen,
            [styles.closedHelpDesk]: !isHelpDeskOpen,
          })}>
          <HelpDesk product={newProduct} helpDeskData={helpDeskData} />
        </div>
        <div
          className={clsx({
            [styles.openHelpDeskButton]: true,
            [styles.openButtonActive]: isHelpDeskOpen,
          })}
          onClick={handleToggleHelpDesk}>
          <p className={styles.helpDesk}>
            <CaretLeftOutlined
              className={clsx(
                styles.helpDeskOpenIcon,
                isHelpDeskOpen && styles.iconOpen
              )}
            />{' '}
            {HELP_DESK}
          </p>
        </div>
      </>

      {showProductModal && (
        <CreateProductModal
          showModalState={showProductModal}
          handleModalCancel={() => setShowProductModal(false)}
          handleModalConfirm={() => setShowProductModal(false)}
          userId={parsedUser?.id}
          isCreate={false}
          callbackAfterSubmit={() => {
            return new Promise(async resolve => {
              await fetchSingleProduct(Number(id));
              resolve('');
            });
          }}
        />
      )}
    </Row>
  );
};

export default FlowContainer;
